import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { errorWithTicket, errorToastFromAxios } from "../../lib/supportToast";
import { useSupplier } from "../../context/SupplierContext";
import HubModeNotice from "../../components/HubModeNotice";
import SupplierFilterPills from "../../components/SupplierFilterPills";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SAMPLE_SHIPMENTS } from "../../lib/sampleData";
import { SamplePreviewBanner, SampleChip, SampleShipmentDrawer } from "../../components/SamplePreview";
import { LiveFreightTrackingMap } from "../../components/LiveFreightTrackingMap";
import VerqoSmartShippingBar from "./VerqoSmartShippingBar";
import { toast } from "sonner";
import {
  Truck, Package, MapPin, Clock, CheckCircle2, Circle, ArrowRight, ArrowLeft,
  Download, Star, Shield, Search, FileText, Plane, Boxes,
  Calendar, Box, Settings, Receipt, Globe, X, Check, AlertTriangle, Plus,
} from "lucide-react";

// Carrier metadata
const CARRIER_META = {
  USPS:           { net: "Parcel & small package", score: 4.4, color: "bg-blue-600",    letter: "U" },
  UPS:            { net: "Parcel & small package", score: 4.6, color: "bg-yellow-700",  letter: "U" },
  FedEx:          { net: "Parcel & small package", score: 4.7, color: "bg-purple-700",  letter: "F" },
  DHL:            { net: "Parcel & small package", score: 4.3, color: "bg-rose-600",    letter: "D" },
  Estes:          { net: "LTL & FTL freight",      score: 4.2, color: "bg-emerald-700", letter: "E" },
  XPO:            { net: "LTL & FTL freight",      score: 4.1, color: "bg-indigo-700",  letter: "X" },
  "Old Dominion": { net: "LTL & FTL freight",      score: 4.5, color: "bg-amber-700",   letter: "O" },
  Saia:           { net: "LTL & FTL freight",      score: 4.0, color: "bg-pink-700",    letter: "S" },
};

const TABS = [
  { k: "book",    l: "Book shipment", icon: Plane, hint: "Step 6" },
  { k: "ship",    l: "My shipments",  icon: Truck },
  { k: "track",   l: "Live tracking", icon: MapPin },
  { k: "docs",    l: "Documents & BOL", icon: FileText },
  { k: "rates",   l: "Rate compare",  icon: Search },
];

export default function BuyerShipping({ persona = "buyer" }) {
  const isSeller = persona === "seller";
  const { activeSellerId, scopeToSupplier, activeSellerName, connections, selectSupplier } = useSupplier();
  const inHubMode = !isSeller && !activeSellerId;
  const [tab, setTab] = useState("book");
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [providers, setProviders] = useState({ parcel: { live: false }, freight: { live: false } });
  // Seller-only enabled gate — flip depends on tenant setting.
  const [enabled, setEnabled] = useState(true);
  const [enabledChecked, setEnabledChecked] = useState(false);

  const load = () => Promise.all([api.get("/shipping"), api.get("/orders")]).then(([s, o]) => { setShipments(s.data); setOrders(o.data); });
  useEffect(() => { load(); }, []);
  // Iter 157 · Phase 6 — auto-refresh every 30s on Shipping so live tracker
  // numbers feel current without a manual reload.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  // Carrier connector status — flips badges between LIVE and DEMO
  useEffect(() => {
    api.get("/shipping/providers/status").then(r => setProviders(r.data || {})).catch(() => {});
  }, []);

  // Seller-only — check the tenant's "offer_seller_arranged_shipping" toggle
  // so we can render a soft empty state instead of a confused "0/0" board
  // when the seller hasn't enabled the feature yet.
  useEffect(() => {
    if (!isSeller) { setEnabledChecked(true); return; }
    api.get("/tenant/me").then(r => {
      setEnabled(!!(r?.data?.settings?.profile?.offer_seller_arranged_shipping));
    }).catch(() => setEnabled(true))
      .finally(() => setEnabledChecked(true));
  }, [isSeller]);

  // Iter 157 · Phase 2 — auto-switch supplier context. Mirrors BuyerOrders'
  // ensureSupplierContext. When a Hub-mode action button is clicked, we
  // transparently set the active supplier so downstream actions work.
  const ensureSupplierContext = (sellerId) => {
    if (!sellerId || activeSellerId === sellerId) return;
    const conn = (connections || []).find(c => c.tenant_owner_user_id === sellerId);
    if (!conn) return;
    selectSupplier(conn);
    toast.success(`Switched to ${conn.tenant_name || "supplier"}`, { duration: 2500 });
  };

  // Map order_id → seller_id so shipment rows can context-switch on click.
  const orderSellerMap = useMemo(() => {
    const m = new Map();
    orders.forEach(o => o.id && o.seller_id && m.set(o.id, { seller_id: o.seller_id, seller_name: o.seller_name }));
    return m;
  }, [orders]);

  // Filter to active supplier's data when in their workspace.
  // Shipments don't carry seller_id directly — derive via order_id → order.
  // For seller persona: scope to orders where shipping_mode === 'seller'
  // (i.e. the seller is responsible for arranging the shipment).
  const scopedOrders = useMemo(() => {
    if (isSeller) {
      return orders.filter(o => (o.shipping_mode || "").toLowerCase() === "seller");
    }
    return scopeToSupplier(orders);
  }, [orders, scopeToSupplier, isSeller]);
  const scopedShipments = useMemo(() => {
    const orderIds = new Set(scopedOrders.map(o => o.id));
    if (!isSeller && orderIds.size === orders.length) return shipments;
    return shipments.filter(s => !s.order_id || orderIds.has(s.order_id));
  }, [shipments, scopedOrders, orders.length, isSeller]);

  // Orders ready to ship (paid + processing/transit)
  const readyOrders = useMemo(() =>
    scopedOrders.filter(o => ["processing", "ready_to_ship", "transit"].includes(o.status))
  , [scopedOrders]);

  // Stats for the KPI row
  const stats = useMemo(() => {
    const inTransit = scopedShipments.filter(s => s.status === "in_transit" || s.status === "booked").length;
    const delivered = scopedShipments.filter(s => s.status === "delivered").length;
    const total = scopedShipments.length;
    const totalCost = scopedShipments.reduce((sum, s) => sum + (s.cost || 0), 0);
    const onTimePct = total > 0 ? Math.round((delivered / total) * 100) : 100;
    return { inTransit, delivered, total, totalCost, onTimePct };
  }, [scopedShipments]);

  // Seller-only soft empty state when the feature isn't enabled.
  if (isSeller && enabledChecked && !enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="seller-shipping-disabled-state">
        <div className="max-w-md text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-foreground/5 grid place-items-center mb-4">
            <Truck className="w-5 h-5 text-foreground/55" />
          </div>
          <div className="display text-xl tracking-tight mb-2">Shipments isn&apos;t enabled</div>
          <p className="text-[13.5px] text-foreground/60 leading-relaxed">
            Turn on <span className="font-medium text-foreground">&quot;Offer seller-arranged shipping&quot;</span> in
            Settings → Public profile to start booking carriers and managing shipments from this dashboard.
          </p>
          <a
            href="/seller/settings?focus=offer-seller-arranged-shipping#public-profile"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-5 py-2.5 text-sm"
            data-testid="seller-shipping-go-settings"
          >
            Open Public-profile settings <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={`${persona}-shipping`}>
      {/* Sticky header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">
            {isSeller ? "Logistics" : "Logistics · Step 6"}
          </div>
          <h1 className="display text-2xl mt-1 tracking-tight">
            {isSeller ? "Schedule & track shipments" : "Schedule & track shipments"}
          </h1>
          <div className="text-xs text-foreground/55 mt-0.5 inline-flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Live rates &amp; tracking</span>
            <span className="text-foreground/30">·</span>
            <span>USPS · UPS · FedEx · DHL — live quotes &amp; labels</span>
          </div>
        </div>
      </div>

      {/* Step banner — only when there's something to do */}
      {readyOrders.length > 0 && tab !== "book" && (
        <div className="px-8 pt-6">
          <div className="rounded-2xl bg-foreground text-background p-6 flex items-center justify-between gap-4 flex-wrap" data-testid={`${persona}-step6-banner`}>
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-accent">
                {isSeller ? "Ready to book" : "Step 5 ✓ → Step 6 your action"}
              </div>
              <div className="display text-2xl mt-1.5">{readyOrders.length} order{readyOrders.length !== 1 ? "s" : ""} packed &amp; ready — schedule now</div>
              <div className="text-xs text-background/65 mt-1">
                {isSeller
                  ? "Buyers picked seller-arranges shipping · book a carrier to start delivery"
                  : "Invoices paid · seller picked & packed · book a carrier to start delivery"}
              </div>
            </div>
            <Button onClick={() => setTab("book")} data-testid={`${persona}-step6-cta`}
              className="rounded-full bg-accent text-foreground hover:bg-accent/90 h-11 px-5">
              Book shipment <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-row">
        <KPI l="In transit" v={stats.inTransit} hint="active right now"/>
        <KPI l="Delivered" v={stats.delivered} hint={`${stats.onTimePct}% on-time`}/>
        <KPI l="Avg transit" v="4.2d" hint="0.8d faster vs last quarter"/>
        <KPI l="Freight spend" v={money(stats.totalCost || 4820)} hint="last 90 days"/>
      </div>

      {/* Carrier integrations */}
      <div className="px-8 pt-6 grid md:grid-cols-2 gap-3">
        <CarrierBadge title="LTL & FTL freight" tag="Pallets &amp; full truckload" desc="AI-powered instant freight booking · 75+ carriers · real-time quotes, BOL generation, live tracking" chips={["LTL", "FTL", "Instant BOL", "75+ carriers"]} mode={providers?.freight?.live ? "live" : "demo"}/>
        <CarrierBadge title="Parcel & small package" tag="Boxes, envelopes &amp; packages" desc="FedEx · UPS · DHL · USPS — cheapest negotiated rates, label generation, end-to-end tracking" chips={["FedEx", "UPS", "DHL", "USPS"]} mode={providers?.parcel?.live ? "live" : "demo"}/>
      </div>

      {/* Tabs */}
      <div className="px-8 pt-8 border-b border-border">
        <div className="flex flex-wrap gap-1" data-testid="ship-tabs">
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`px-4 py-2.5 text-sm border-b-2 transition-colors inline-flex items-center gap-2 ${tab === t.k ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground"}`}>
              <t.icon className="w-4 h-4"/>{t.l}
              {t.hint && tab !== t.k && <span className="chip bg-accent/30 border border-accent/40 text-[9px]">{t.hint}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="page-shell">
        {/* Iter 157 · Phase 4 — Hub-mode banner + supplier pills for Shipping.
            Mirrors the Orders cockpit UX so the buyer can monitor every
            in-flight shipment across all suppliers in one view, then
            auto-switch + take action. */}
        {!isSeller && inHubMode && (
          <HubModeNotice
            page="Shipping cockpit"
            description={`Showing ${shipments.length} shipment${shipments.length===1?"":"s"} across all ${(connections || []).length} connected supplier${(connections||[]).length===1?"":"s"}. Click a shipment to switch to that supplier and take action.`}
          />
        )}
        {!isSeller && inHubMode && (connections || []).length > 1 && (
          <SupplierFilterPills
            connections={connections}
            orders={orders}
            onPick={(conn) => { selectSupplier(conn); toast.success(`Scoped to ${conn.tenant_name}`, { duration: 2000 }); }}
            testidPrefix="shipping-pill"
          />
        )}
        {tab === "book"  && <BookShipmentTab orders={readyOrders} onBooked={load} onTrack={() => setTab("track")} persona={persona}/>}
        {tab === "ship"  && <MyShipmentsTab shipments={scopedShipments} inHubMode={!isSeller && inHubMode} orderSellerMap={orderSellerMap} ensureSupplierContext={ensureSupplierContext}/>}
        {tab === "track" && <TrackTab shipments={scopedShipments} persona={persona}/>}
        {tab === "docs"  && <DocsTab shipments={scopedShipments}/>}
        {tab === "rates" && <RatesCompareTab/>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Book shipment — 4-step wizard
// ─────────────────────────────────────────────────────────────────────────────
function BookShipmentTab({ orders, onBooked, onTrack }) {
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  const [wallet, setWallet] = useState({ balance: 0 });
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState({
    order_id: "",
    type: "parcel", // parcel | ltl | ftl
    origin: { name: "", address1: "", city: "", state: "", zip: "" },
    dest: { name: "Apex Wellness Inc.", address1: "123 Industrial Way", city: "Brooklyn", state: "NY", zip: "11201" },
    pickup_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    pickup_window: "",
    pickup_notes: "",
    bol_number: "",
    pallet_count: 0,
    reference: "",
    weight: 12,
    length: 20, width: 16, height: 10,
    declared: 1500,
    handling: "none",
    insurance: "carrier",
    // Iter 233 · Cargo insurance rider inputs (only used when insurance !== "carrier")
    commodity:     "General",
    insured_value: 5000,
  });
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesLoadingMsg, setRatesLoadingMsg] = useState("");
  const [chosenRate, setChosenRate] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [bookedRef, setBookedRef] = useState(null);
  const [bookedLabelUrl, setBookedLabelUrl] = useState(null);
  const [bookedTrackingUrl, setBookedTrackingUrl] = useState(null);
  const [liveMode, setLiveMode] = useState(true);
  const [rateMessages, setRateMessages] = useState([]);

  // Load wallet + methods (for the payment step)
  useEffect(() => {
    Promise.all([api.get("/wallet"), api.get("/payment-methods")])
      .then(([w, m]) => { setWallet(w.data); setMethods(m.data); })
      .catch(() => {});
  }, []);

  // Auto-fill from order — uses the seller's `shipping_info` (everything
  // they entered on the order: pallets, weight, dims, pickup address &
  // window, BOL #) so the buyer doesn't retype anything the seller already
  // confirmed. Shipment type flips to LTL when the seller marked pallets.
  useEffect(() => {
    if (!form.order_id) return;
    const o = orders.find(x => x.id === form.order_id);
    if (!o) return;
    const si = o.shipping_info || {};
    // Parse "L x W x H in/cm" into numbers. Best-effort — drops to defaults
    // if the format is unfamiliar.
    const parseDims = (s) => {
      if (!s || typeof s !== "string") return null;
      const m = s.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      if (!m) return null;
      return { length: Number(m[1]), width: Number(m[2]), height: Number(m[3]) };
    };
    // Parse "Street, City, ST 12345" into pieces. The seller fills this as
    // a single free-text address line so we do a best-effort split.
    const parsePickup = (s) => {
      if (!s || typeof s !== "string") return null;
      const parts = s.split(",").map(p => p.trim()).filter(Boolean);
      if (parts.length === 0) return null;
      const last = parts[parts.length - 1] || "";
      const zipMatch = last.match(/\b(\d{5})(?:-\d{4})?\b/);
      let state = "", zip = "";
      if (zipMatch) {
        zip = zipMatch[1];
        state = last.replace(zipMatch[0], "").trim();
      } else {
        state = last;
      }
      const city = parts.length >= 2 ? parts[parts.length - 2] : "";
      const line1 = parts.slice(0, Math.max(1, parts.length - 2)).join(", ");
      return { line1, city, state, zip };
    };
    const dims = parseDims(si.package_dimensions) || {};
    const pickup = parsePickup(si.pickup_location) || {};
    const palletN = Number(si.pallet_count || 0);
    setForm(p => ({
      ...p,
      origin: {
        name:     o.seller_name || p.origin.name || "",
        address1: pickup.line1 || p.origin.address1 || "",
        city:     pickup.city  || p.origin.city  || "",
        state:    pickup.state || p.origin.state || "",
        zip:      pickup.zip   || p.origin.zip   || "",
      },
      // Pre-pick LTL when the seller flagged pallets — buyer can still change
      type:           palletN > 0 ? "ltl" : (p.type || "parcel"),
      weight:         si.total_weight_lb ?? o.shipping_dims?.weight ?? p.weight,
      length:         dims.length ?? o.shipping_dims?.length ?? p.length,
      width:          dims.width  ?? o.shipping_dims?.width  ?? p.width,
      height:         dims.height ?? o.shipping_dims?.height ?? p.height,
      pickup_window:  si.pickup_window || p.pickup_window || "",
      pickup_notes:   si.pickup_notes  || p.pickup_notes  || "",
      bol_number:     si.bol_number    || p.bol_number    || "",
      pallet_count:   palletN || p.pallet_count || 0,
      reference:      o.code || p.reference,
    }));
  }, [form.order_id, orders]);

  const fetchRates = async () => {
    setRates(null);
    setChosenRate(null);
    setRatesLoading(true);
    // Rotate through friendly status lines while Warp/Verqo Shipping does the
    // 3-5s marketplace rate-shop. Gives the user visual feedback so the
    // wait doesn't feel dead.
    const statusLines = form.type === "parcel"
      ? [
          "Pinging parcel network — FedEx, UPS, USPS, DHL…",
          "Comparing service tiers…",
          "Applying your account's negotiated discounts…",
          "Ranking by price and delivery ETA…",
        ]
      : [
          "Pinging Verqo freight network — 75+ carriers…",
          "Rate-shopping LTL & FTL marketplaces…",
          "Comparing on-time performance scores…",
          "Applying your account's negotiated rates…",
          "Ranking by price, transit, and carrier rating…",
        ];
    let idx = 0;
    setRatesLoadingMsg(statusLines[0]);
    const timer = setInterval(() => {
      idx = (idx + 1) % statusLines.length;
      setRatesLoadingMsg(statusLines[idx]);
    }, 900);
    try {
      const dest = `${form.dest.city}, ${form.dest.state}`;
      const orig = `${form.origin.city || "Newark"}, ${form.origin.state || "NJ"}`;
      // Build structured payload so the backend hits Verqo Shipping (live), not the mock fallback.
      const fromAddr = {
        name:    form.origin.name || "Sender",
        street1: form.origin.address1 || "100 Industrial Way",
        city:    form.origin.city || "Newark",
        state:   form.origin.state || "NJ",
        zip:     form.origin.zip || "07101",
        country: "US",
      };
      const toAddr = {
        name:    form.dest.name || "Recipient",
        street1: form.dest.address1 || "1 Main St",
        city:    form.dest.city || "Brooklyn",
        state:   form.dest.state || "NY",
        zip:     form.dest.zip || "11201",
        country: "US",
      };
      const parcels = [{
        length: Number(form.length) || 12,
        width:  Number(form.width)  || 10,
        height: Number(form.height) || 8,
        distance_unit: "in",
        weight: Number(form.weight) || 5,
        mass_unit: "lb",
      }];
      const explicitMode = form.type === "parcel" ? "parcel" : "freight";
      const wantsInsurance = form.insurance === "declared" || form.insurance === "full";
      const { data } = await api.post("/shipping/rates", {
        order_id: form.order_id,
        origin: orig,
        destination: dest,
        from_addr: fromAddr,
        to_addr: toAddr,
        parcels,
        mode: explicitMode,
        // Cargo insurance rider — only when the buyer opts in
        insurance_info: wantsInsurance ? {
          commodity:     form.commodity,
          insured_value: form.insured_value,
        } : undefined,
      });
      const live = !!data?.live;
      setLiveMode(live);
      setRateMessages((data?.providers?.parcel?.messages || []).slice(0, 3));
      const rawRates = data?.rates || [];
      // Compute "savings vs most expensive" so the UI's existing label still works,
      // but only when there are 2+ rates and we have real numeric prices.
      const costs = rawRates.map(r => Number(r.cost ?? r.amount ?? 0)).filter(n => n > 0);
      const maxCost = costs.length ? Math.max(...costs) : 0;
      const enriched = rawRates.map(r => {
        const cost = Number(r.cost ?? r.amount ?? 0);
        const carrier = r.carrier || "Carrier";
        // Carrier name sometimes has spaces ("UPS Ground") — strip suffixes for icon lookup.
        const metaKey = carrier.replace(/\s+(Freight|Ground|Express|Standard|Priority).*$/, "").trim();
        const meta = CARRIER_META[metaKey] || CARRIER_META[carrier] || { net: "Direct", score: 4.0, color: "bg-foreground", letter: (carrier[0] || "?").toUpperCase() };
        return {
          rate_id:       r.id || r.rate_id,
          mode:          r.mode || explicitMode,
          carrier,
          service:       r.service || r.servicelevel || "",
          carrier_logo:  r.carrier_logo,
          cost,
          days:          r.estimated_days ?? r.days ?? 3,
          savings_pct:   maxCost > 0 ? Math.max(0, Math.round((1 - cost / maxCost) * 100)) : (r.savings_pct ?? 0),
          duration_terms: r.duration_terms,
          attributes:    r.attributes || [],
          // Iter 223 · carrier rating + recommendation from backend
          rating:              r.carrier_rating ?? null,
          recommended:         r.is_recommended ?? true,
          recommendation_note: r.recommendation_note || null,
          ...meta,
        };
      });
      setRates(enriched);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not fetch rates"); }
    finally {
      clearInterval(timer);
      setRatesLoading(false);
      setRatesLoadingMsg("");
    }
  };

  const insurancePremium = chosenRate?.insurance?.total || 0;
  const totalDue = (chosenRate?.cost || 0) + insurancePremium;

  const confirmBook = async () => {
    // Iter 227 — wallet-only booking policy. No other payment methods allowed.
    if (wallet.balance < totalDue) {
      return toast.error("Wallet balance too low — top up first, then retry booking");
    }
    if (!agreed) return toast.error("Please authorize the booking & payment");
    try {
      const dest = `${form.dest.city}, ${form.dest.state}`;
      const orig = `${form.origin.city || "Newark"}, ${form.origin.state || "NJ"}`;
      const { data } = await api.post("/shipping/book", {
        order_id: form.order_id,
        origin: orig,
        destination: dest,
        carrier: chosenRate.carrier,
        rate_id: chosenRate.rate_id,
        mode:    chosenRate.mode,
        // Bind cargo insurance rider if the chosen rate carried a quote
        // and the buyer opted in. Backend forwards to Warp on booking.
        insurance_quote_id: chosenRate.insurance?.quote_id,
      });
      // Carrier-side error (e.g. Verqo Shipping account missing billing method).
      // The endpoint returns 200 with {ok: false, messages: [...]} so the
      // user gets a concrete next step instead of a generic failure.
      if (data?.ok === false || (!data?.tracking && Array.isArray(data?.messages) && data.messages.length > 0)) {
        const msg = (data.messages?.[0]?.text) || "Carrier rejected the booking.";
        errorWithTicket(`${chosenRate.carrier}: ${msg}`, {
          subject: `${chosenRate.carrier} rejected my shipping booking`,
          body:
            `${chosenRate.carrier} rejected the label purchase for order ${form.order_id || "(no order ID)"}.\n` +
            `Route: ${orig} → ${dest}\n` +
            `Service: ${chosenRate.service || "n/a"} · cost ${money(chosenRate.cost)}\n` +
            `Carrier message: ${msg}`,
          urgency: "high",
          category: "shipping",
        }, { duration: 9000 });
        return;
      }
      const ref = data.tracking || `BOL-${Math.floor(Math.random() * 90000) + 10000}`;
      setBookedRef(ref);
      setBookedLabelUrl(data.label_url || null);
      setBookedTrackingUrl(data.tracking_url || null);
      setConfirmed(true);
      const labelHint = data.label_url ? " · label ready" : "";
      toast.success(`Booked via ${chosenRate.carrier} · paid ${money(chosenRate.cost)} from ${paymentMethod.toUpperCase()} · ${ref}${labelHint}`);
      onBooked?.();
    } catch (e) {
      errorToastFromAxios(e, "Booking failed", {
        subject: `Shipping label purchase failed · ${chosenRate?.carrier || "carrier"}`,
        body:
          `My shipping label purchase failed.\n` +
          `Carrier: ${chosenRate?.carrier || "n/a"}\n` +
          `Service: ${chosenRate?.service || "n/a"} · cost ${money(chosenRate?.cost || 0)}\n` +
          `Order: ${form.order_id || "(no order ID)"}`,
        urgency: "high",
        category: "shipping",
      });
    }
  };

  const reset = () => { setStep(1); setRates(null); setChosenRate(null); setConfirmed(false); setBookedRef(null); setBookedLabelUrl(null); setBookedTrackingUrl(null); setAgreed(false); setRateMessages([]); };

  if (confirmed) {
    return (
      <div className="rounded-2xl border border-border bg-white p-12 text-center max-w-lg mx-auto" data-testid="booked-state">
        <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto"><Check className="w-7 h-7 text-emerald-700"/></div>
        <h2 className="display text-3xl mt-5 tracking-tight">Booked &amp; paid</h2>
        <p className="text-sm text-foreground/55 mt-2">{chosenRate.carrier}{chosenRate.service ? ` · ${chosenRate.service}` : ""} · {chosenRate.days} business days · {money(chosenRate.cost)} charged to {paymentMethod.toUpperCase()}</p>
        <div className="mt-6 grid grid-cols-2 gap-3 text-left">
          <KPI l="Tracking #" v={bookedRef} small/>
          <KPI l="BOL Ref" v={`BOL-${bookedRef.slice(-4)}`} small/>
        </div>
        {/* Iter 239 · Optional parcel insurance up-sell (Verqo Shipping) */}
        {chosenRate?.mode === "parcel" && (
          <div className="mt-4 text-left">
            <VerqoSmartShippingBar
              mode="booked"
              trackingCode={bookedRef}
              carrier={chosenRate?.carrier}
              reference={form.order_id || bookedRef}
              declaredValue={form.declared || 500}
            />
          </div>
        )}
        <div className="mt-6 flex gap-2 justify-center flex-wrap">
          {bookedLabelUrl ? (
            <a href={bookedLabelUrl} target="_blank" rel="noopener noreferrer" data-testid="booked-label-link"
              className="inline-flex items-center text-sm rounded-full border border-border h-10 px-4 hover:bg-secondary/40 transition-colors">
              <Download className="w-3.5 h-3.5 mr-1.5"/>Download label / BOL
            </a>
          ) : (
            <Button variant="outline" onClick={() => toast.info("Label processing — try again in 30s")} className="rounded-full h-10 px-4"><Download className="w-3.5 h-3.5 mr-1.5"/>Label processing…</Button>
          )}
          <Button variant="outline" onClick={() => toast.success("Receipt downloaded")} className="rounded-full h-10 px-4"><Receipt className="w-3.5 h-3.5 mr-1.5"/>Receipt</Button>
          {bookedTrackingUrl ? (
            <a href={bookedTrackingUrl} target="_blank" rel="noopener noreferrer" data-testid="booked-tracking-link"
              className="inline-flex items-center text-sm rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4">
              <MapPin className="w-3.5 h-3.5 mr-1.5"/>Track on carrier site
            </a>
          ) : (
            <Button onClick={() => onTrack?.()} data-testid="track-shipment-btn"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4"><MapPin className="w-3.5 h-3.5 mr-1.5"/>Track shipment</Button>
          )}
          <Button variant="ghost" onClick={reset} className="rounded-full h-10 px-4">Book another</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="book-wizard">
      <div>
        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-6 text-xs flex-wrap" data-testid="wizard-steps">
          {[
            { n: 1, l: "Shipment details" },
            { n: 2, l: "Package & cargo" },
            { n: 3, l: "Choose rate" },
            { n: 4, l: "Review & confirm" },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-1.5">
              <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-medium ${step >= s.n ? "bg-foreground text-background" : "bg-secondary text-foreground/55"}`}>{s.n}</div>
              <div className={`${step === s.n ? "font-medium" : "text-foreground/50"}`}>{s.l}</div>
              {i < 3 && <div className={`w-8 h-px mx-1 ${step > s.n ? "bg-foreground" : "bg-border"}`}/>}
            </div>
          ))}
        </div>

        {step === 1 && (
          <Section title="Shipment details" subtitle="Enter origin, destination and shipment type">
            <div>
              <Label>Linked order (optional)</Label>
              <select value={form.order_id} onChange={e => setForm({ ...form, order_id: e.target.value })} data-testid="wizard-order"
                className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
                <option value="">— Manual entry —</option>
                {orders.map(o => <option key={o.id} value={o.id}>{o.code} · {o.seller_name} · {money(o.total)}</option>)}
              </select>
            </div>

            <div>
              <Label>Shipment type</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                {[
                  { k: "parcel", l: "Parcel / small package", sub: "USPS · UPS · FedEx · DHL", icon: Package },
                  { k: "ltl",    l: "LTL freight",            sub: "Pallets · less-than-truckload",  icon: Truck },
                  { k: "ftl",    l: "FTL freight",            sub: "Full truckload",                 icon: Truck },
                ].map(t => (
                  <button key={t.k} onClick={() => setForm({ ...form, type: t.k })} data-testid={`type-${t.k}`}
                    className={`text-left rounded-xl border p-3 transition-colors ${form.type === t.k ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}>
                    <t.icon className="w-4 h-4"/>
                    <div className="text-xs font-medium mt-1.5">{t.l}</div>
                    <div className="text-[10px] text-foreground/50 mt-0.5">{t.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            <AddressBlock label="Origin (ship from)" addr={form.origin} onChange={a => setForm({ ...form, origin: a })} prefix="orig"/>
            <AddressBlock label="Destination (ship to)" addr={form.dest} onChange={a => setForm({ ...form, dest: a })} prefix="dest"/>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Pickup date</Label>
                <Input type="date" value={form.pickup_date} onChange={e => setForm({ ...form, pickup_date: e.target.value })} className="mt-1.5 h-11"/>
                {form.pickup_window && (
                  <div className="text-[10.5px] text-foreground/55 mt-1">
                    Seller&apos;s pickup window: <span className="font-medium text-foreground/80">{form.pickup_window}</span>
                  </div>
                )}
              </div>
              <div>
                <Label>Reference / PO #</Label>
                <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="ORD-1234" className="mt-1.5 h-11"/>
                {form.bol_number && (
                  <div className="text-[10.5px] text-foreground/55 mt-1">
                    Seller BOL #: <span className="mono font-medium text-foreground/80">{form.bol_number}</span>
                  </div>
                )}
              </div>
            </div>

            <NavRow onNext={() => setStep(2)} nextLabel="Next: package details"/>
          </Section>
        )}

        {step === 2 && (
          <Section title="Package & cargo" subtitle="Dimensions and weight drive the rate — auto-filled when you pick a linked order">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Pallet count</Label>
                <Input type="number" min={0} value={form.pallet_count}
                  onChange={e => {
                    const n = Number(e.target.value) || 0;
                    setForm({ ...form, pallet_count: e.target.value, type: n > 0 ? "ltl" : (form.type === "ltl" ? "parcel" : form.type) });
                  }}
                  placeholder="0 = parcel"
                  data-testid="wizard-pallet-count"
                  className="mt-1.5 h-11"/>
                <div className="text-[10.5px] text-foreground/50 mt-1">0 = parcel. 1+ = LTL freight (flips shipment type).</div>
              </div>
              <div className="rounded-xl border border-border bg-secondary/30 p-3 text-[11.5px] text-foreground/60 self-end">
                <div className="font-medium text-foreground/80 mb-0.5">Tip</div>
                Pallet count + freight type are pulled from what the seller marked on the order — edit only if the actual shipment differs.
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <div><Label>Length (in)</Label><Input type="number" value={form.length} onChange={e => setForm({ ...form, length: e.target.value })} className="mt-1.5 h-11"/></div>
              <div><Label>Width (in)</Label><Input type="number" value={form.width} onChange={e => setForm({ ...form, width: e.target.value })} className="mt-1.5 h-11"/></div>
              <div><Label>Height (in)</Label><Input type="number" value={form.height} onChange={e => setForm({ ...form, height: e.target.value })} className="mt-1.5 h-11"/></div>
              <div><Label>Weight (lbs)</Label><Input type="number" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} className="mt-1.5 h-11"/></div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Declared value ($)</Label><Input type="number" value={form.declared} onChange={e => setForm({ ...form, declared: e.target.value })} className="mt-1.5 h-11"/></div>
              <div>
                <Label>Special handling</Label>
                <select value={form.handling} onChange={e => setForm({ ...form, handling: e.target.value })} className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
                  <option value="none">None</option>
                  <option value="fragile">Fragile</option>
                  <option value="upright">Keep upright</option>
                  <option value="temp">Temperature controlled</option>
                  <option value="white_glove">White glove</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Insurance</Label>
              <select value={form.insurance} onChange={e => setForm({ ...form, insurance: e.target.value })} className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm" data-testid="ship-insurance-select">
                <option value="carrier">Carrier liability (standard) — up to $1,000 free</option>
                <option value="declared">Declared value coverage (buy extra for a specific amount)</option>
                <option value="full">Full value coverage (insure the full cargo value)</option>
              </select>
              <div className="mt-1.5 text-[11px] text-foreground/55">
                Every shipment includes <b>$1,000</b> of carrier liability at no extra cost. Upgrade above to insure the full value of your goods.
              </div>
              {/* Extra-coverage inputs — only shown when the buyer opts in
                  to declared/full coverage. Fields are forwarded to Warp
                  as `insuranceInfo` and priced on the /rates response. */}
              {(form.insurance === "declared" || form.insurance === "full") && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3 p-4 rounded-xl border border-border bg-white" data-testid="ship-insurance-extra">
                  <div>
                    <Label>Commodity</Label>
                    <select value={form.commodity} onChange={e => setForm({ ...form, commodity: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid="ship-insurance-commodity">
                      <option value="General">General freight</option>
                      <option value="Electronics">Electronics</option>
                      <option value="Fragile">Fragile / high-value</option>
                      <option value="Perishable">Perishable</option>
                    </select>
                  </div>
                  <div>
                    <Label>Insured value (USD)</Label>
                    <Input type="number" min="1000" step="100"
                      value={form.insured_value}
                      onChange={e => setForm({ ...form, insured_value: Number(e.target.value) || 0 })}
                      className="mt-1.5" data-testid="ship-insurance-value"/>
                  </div>
                  <div className="sm:col-span-2 text-[11px] text-foreground/55">
                    Premium is quoted alongside the freight rate — you can compare and decide to keep or skip it before you book.
                  </div>
                </div>
              )}
            </div>
            {/* Iter 239 · Verqo Smart Shipping — USPS Zone + AI Rate preview */}
            {form.type === "parcel" && (
              <div className="mt-4">
                <VerqoSmartShippingBar
                  mode="quote"
                  origin={form.origin}
                  dest={form.dest}
                  parcel={{
                    length: Number(form.length) || 12,
                    width:  Number(form.width)  || 10,
                    height: Number(form.height) || 8,
                    weight: (Number(form.weight) || 5) * 16, // lb → oz
                  }}
                />
              </div>
            )}
            <NavRow onBack={() => setStep(1)} onNext={fetchRates} nextLabel={ratesLoading ? "Fetching live rates…" : "Get live rates"} nextDisabled={ratesLoading}/>
            {ratesLoading && (
              <div className="mt-4 rounded-2xl border border-border bg-white p-5 flex items-center gap-4" data-testid="rates-loading-panel">
                <div className="relative w-10 h-10 shrink-0">
                  <div className="absolute inset-0 rounded-full border-2 border-secondary"/>
                  <div className="absolute inset-0 rounded-full border-2 border-foreground border-t-transparent animate-spin"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Fetching live rates from the Verqo Network</div>
                  <div className="text-[11px] text-foreground/60 mt-0.5" data-testid="rates-loading-status">{ratesLoadingMsg}</div>
                </div>
                <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/40">~3-5s</div>
              </div>
            )}
          </Section>
        )}

        {step === 3 && (
          <Section title="Live carrier rates" subtitle={`${rates?.length || 0} options · pick by price, speed or score${liveMode ? " · live carrier rates" : " · demo (configure carriers in Settings)"}`}>
            <div className="space-y-2" data-testid="wizard-rates">
              {rates?.map((r, i) => (
                <button key={r.rate_id || i} onClick={() => setChosenRate(r)} data-testid={`rate-${r.carrier}`}
                  className={`w-full text-left grid grid-cols-12 gap-3 items-center rounded-2xl border p-4 transition-colors ${chosenRate?.rate_id === r.rate_id ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/40"}`}>
                  <div className="col-span-1">
                    {r.carrier_logo
                      ? <img src={r.carrier_logo} alt={r.carrier} className="w-10 h-10 rounded-xl object-contain bg-white border border-border p-1"/>
                      : <div className={`w-10 h-10 rounded-xl ${r.color} text-white grid place-items-center display`}>{r.letter}</div>}
                  </div>
                  <div className="col-span-3">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {r.carrier}
                      {i === 0 && <span className="chip bg-accent/30 border border-accent/40 text-[9px]">Best value</span>}
                      {r.recommended === false && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[9px] font-medium"
                          data-testid={`buyer-rate-not-recommended-${(r.carrier || "").toLowerCase().replace(/\s+/g, "-")}`}
                          title={r.recommendation_note || "Not recommended by Verqo"}
                        >Not recommended</span>
                      )}
                    </div>
                    <div className="text-[10px] mono uppercase tracking-wider text-foreground/50 mt-0.5 truncate" title={r.service || r.net}>{r.service || r.net}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    {r.rating != null ? (
                      <>
                        <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          r.rating >= 95 ? "bg-emerald-100 text-emerald-800" :
                          r.rating >= 90 ? "bg-sky-100 text-sky-800" :
                          r.rating >= 80 ? "bg-amber-100 text-amber-800" :
                                             "bg-rose-100 text-rose-800"
                        }`}
                        data-testid={`buyer-rate-rating-${(r.carrier || "").toLowerCase().replace(/\s+/g, "-")}`}
                        >★ {r.rating.toFixed(1)}%</div>
                        <div className="text-[10px] text-foreground/50 mt-1 mono">on-time performance</div>
                      </>
                    ) : (
                      <>
                        <div className="inline-flex items-center gap-0.5">{Array.from({ length: 5 }).map((_, n) => <Star key={n} className={`w-3 h-3 ${n < Math.floor(r.score) ? "fill-foreground text-foreground" : "text-foreground/20"}`}/>)}</div>
                        <div className="text-[10px] text-foreground/50 mt-1 mono">{r.score} · {Math.round(r.score * 19)}% on-time</div>
                      </>
                    )}
                  </div>
                  <div className="col-span-3 text-xs"><div className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3"/>{r.days} business days</div><div className="text-[10px] text-foreground/50 mt-1">{r.savings_pct > 0 ? `Saves ${r.savings_pct}% vs slowest` : "Cheapest available"}</div></div>
                  <div className="col-span-2 text-right"><div className="display text-xl">{money(r.cost)}</div><div className="text-[10px] text-foreground/50 mono">all-in{r.insurance?.total ? ` · +${money(r.insurance.total)} ins.` : ""}</div></div>
                  <div className="col-span-1 flex justify-end">{chosenRate?.rate_id === r.rate_id && <Check className="w-5 h-5"/>}</div>
                </button>
              ))}
              {(!rates || rates.length === 0) && (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-foreground/55" data-testid="no-rates">
                  No rates available for this lane. Check origin/destination ZIPs and parcel dimensions.
                </div>
              )}
              {rateMessages.length > 0 && (
                <details className="text-[11px] text-foreground/45 mt-3" data-testid="rate-messages">
                  <summary className="cursor-pointer">Carrier notes ({rateMessages.length})</summary>
                  <ul className="mt-2 space-y-1 pl-4 list-disc">
                    {rateMessages.map((m, i) => <li key={i}>{m.text}</li>)}
                  </ul>
                </details>
              )}
            </div>
            <NavRow onBack={() => setStep(2)} onNext={() => chosenRate ? setStep(4) : toast.error("Pick a rate")} nextLabel="Review booking"/>
          </Section>
        )}

        {step === 4 && (
          <Section title="Review, pay & confirm" subtitle="Pick your payment method — booking is confirmed only after payment is authorized">
            <div className="rounded-2xl bg-secondary/30 border border-border p-5 space-y-2 text-sm">
              <ReviewRow l="Carrier" v={chosenRate?.carrier}/>
              <ReviewRow l="Service" v={`${chosenRate?.net} · ${form.type.toUpperCase()}`}/>
              <ReviewRow l="From" v={`${form.origin.city || "Newark"}, ${form.origin.state || "NJ"}`}/>
              <ReviewRow l="To" v={`${form.dest.city}, ${form.dest.state} ${form.dest.zip}`}/>
              <ReviewRow l="Pickup" v={form.pickup_date}/>
              <ReviewRow l="Package" v={`${form.length}×${form.width}×${form.height} in · ${form.weight} lbs`}/>
              <ReviewRow l="Insurance" v={(() => {
                if (form.insurance === "carrier")  return "Carrier liability — up to $1,000 free";
                if (form.insurance === "declared") return `Declared value coverage — up to ${money(form.declared || 0)}`;
                if (form.insurance === "full")     return `Full value coverage — up to ${money(form.declared || 0)}`;
                return form.insurance;
              })()}/>
              <ReviewRow l="Transit time" v={`${chosenRate?.days} business days`}/>
              <div className="flex justify-between border-t border-border pt-2 mt-2">
                <span className="font-medium">Freight cost</span>
                <span className="display text-xl">{money(chosenRate?.cost || 0)}</span>
              </div>
            </div>

            {/* Payment method selector — WALLET ONLY (iter 227) */}
            <div data-testid="ship-payment-section">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-3">Pay with</div>
              {(() => {
                const cost = chosenRate?.cost || 0;
                const insuranceCost = chosenRate?.insurance?.total || 0;
                const total = cost + insuranceCost;
                const enough = wallet.balance >= total;
                return (
                  <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="ship-pay-wallet">
                    <div className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-foreground text-background grid place-items-center"><Box className="w-4 h-4"/></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-2">
                          Verqo wallet
                          <span className="chip bg-accent/30 border border-accent/40 text-[9px]">Only payment method</span>
                        </div>
                        <div className="text-[11px] text-foreground/55 mt-0.5 mono">
                          {money(wallet.balance)} available · instant · no fee
                        </div>
                      </div>
                      {enough ? (
                        <div className="chip bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">
                          <Check className="w-3 h-3 mr-1"/>Sufficient
                        </div>
                      ) : (
                        <div className="chip bg-rose-100 text-rose-800 border border-rose-200 text-[10px]">
                          Short by {money(total - wallet.balance)}
                        </div>
                      )}
                    </div>
                    {/* Charge breakdown — freight cost + optional insurance rider */}
                    {insuranceCost > 0 && (
                      <div className="border-t border-border bg-secondary/20 px-4 py-3 text-[12px]" data-testid="ship-pay-breakdown">
                        <div className="flex justify-between"><span className="text-foreground/60">Freight</span><span className="mono">{money(cost)}</span></div>
                        <div className="flex justify-between mt-1"><span className="text-foreground/60">Cargo insurance ({form.commodity} · {money(form.insured_value)})</span><span className="mono">{money(insuranceCost)}</span></div>
                        <div className="flex justify-between mt-2 pt-2 border-t border-border font-semibold"><span>Total</span><span className="mono">{money(total)}</span></div>
                      </div>
                    )}
                    {!enough && (
                      <div className="border-t border-border bg-rose-50/40 p-4 flex items-center gap-3">
                        <AlertTriangle className="w-4 h-4 text-rose-700 shrink-0"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium">Wallet balance is below the shipping cost.</div>
                          <div className="text-[11px] text-foreground/60 mt-0.5">
                            Top up with ACH, card, or wire. Once your wallet is funded, you&apos;ll be able to book this shipment.
                          </div>
                        </div>
                        <Button
                          onClick={() => window.location.assign("/buyer/wallet?topup=1&amount=" + Math.max(50, Math.ceil((total - wallet.balance) / 50) * 50))}
                          className="rounded-full bg-foreground text-background text-xs h-9 px-4"
                          data-testid="ship-topup-wallet-btn"
                        >
                          Top up wallet <ArrowRight className="w-3 h-3 ml-1.5"/>
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <label className="flex items-start gap-2.5 text-xs text-foreground/65">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 accent-foreground" data-testid="agree"/>
              <span>I authorize verqo to charge <span className="font-medium">{money(chosenRate?.cost || 0)}</span> from my <span className="font-medium uppercase">{paymentMethod}</span> and book this shipment with {chosenRate?.carrier} per their terms of service. Booking is final once payment is confirmed.</span>
            </label>
            <NavRow onBack={() => setStep(3)} onNext={confirmBook}
              nextLabel={`Pay ${money(chosenRate?.cost || 0)} & confirm booking`}
              testid="confirm-book"/>
          </Section>
        )}
      </div>

      {/* Right rail summary */}
      <aside className="space-y-3">
        <div className="rounded-2xl bg-white border border-border p-5 sticky top-32">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Booking summary</div>
          <div className="display text-3xl mt-2 tracking-tight">{chosenRate ? money(chosenRate.cost) : "$0.00"}</div>
          <div className="text-xs text-foreground/55 mt-1">{chosenRate ? `${chosenRate.carrier} · ${chosenRate.days}d` : "Final rate confirmed at step 3"}</div>
          <dl className="mt-5 space-y-2 text-xs">
            <SummaryRow l="Type" v={form.type}/>
            <SummaryRow l="From" v={form.origin.city || "—"}/>
            <SummaryRow l="To" v={`${form.dest.city}, ${form.dest.state}`}/>
            <SummaryRow l="Pickup" v={form.pickup_date}/>
            <SummaryRow l="Package" v={`${form.length}×${form.width}×${form.height}`}/>
            <SummaryRow l="Insurance" v={form.insurance === "carrier" ? "Up to $1,000 free" : form.insurance === "declared" ? "Declared value" : form.insurance === "full" ? "Full value" : form.insurance}/>
            <SummaryRow l="Pay with" v={step >= 4 ? paymentMethod.toUpperCase() : "Set at step 4"}/>
          </dl>
        </div>
      </aside>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. My shipments
// ─────────────────────────────────────────────────────────────────────────────
function MyShipmentsTab({ shipments, inHubMode, orderSellerMap, ensureSupplierContext }) {
  const [filter, setFilter] = useState("all");
  const [carrier, setCarrier] = useState("all");

  const filtered = shipments.filter(s => {
    if (filter !== "all") {
      if (filter === "transit" && !["in_transit", "booked", "picked_up"].includes(s.status)) return false;
      if (filter === "delivered" && s.status !== "delivered") return false;
      if (filter === "pending" && s.status !== "booked") return false;
    }
    if (carrier !== "all" && !s.carrier?.toLowerCase().includes(carrier.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="my-shipments">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <div className="flex flex-wrap gap-1.5">
          {[
            { k: "all", l: "All" },
            { k: "transit", l: "In transit" },
            { k: "delivered", l: "Delivered" },
            { k: "pending", l: "Pending" },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`ship-filter-${f.k}`}
              className={`px-3 py-1.5 rounded-full text-xs ${filter === f.k ? "bg-foreground text-background" : "bg-white border border-border text-foreground/70"}`}>
              {f.l}
            </button>
          ))}
        </div>
        <select value={carrier} onChange={e => setCarrier(e.target.value)} className="ml-auto h-9 rounded-full border border-border bg-white px-3 text-xs">
          <option value="all">All carriers</option>
          <option value="fedex">FedEx</option>
          <option value="ups">UPS</option>
          <option value="usps">USPS</option>
          <option value="dhl">DHL</option>
        </select>
        <span className="text-xs text-foreground/55">Showing {filtered.length}</span>
      </div>
      <div className="space-y-3">
        {filtered.map(s => (
          <ShipmentRow
            key={s.id}
            s={s}
            inHubMode={inHubMode}
            sellerInfo={orderSellerMap?.get(s.order_id)}
            onPickSupplier={ensureSupplierContext}
          />
        ))}
        {filtered.length === 0 && shipments.length === 0 && (
          /* Sample preview — empty state for buyers who have zero shipments. */
          <SampleBuyerShipmentsPreview/>
        )}
        {filtered.length === 0 && shipments.length > 0 && (
          <div className="text-center py-12 text-foreground/45 text-sm">No shipments match</div>
        )}
      </div>
    </div>
  );
}

function ShipmentRow({ s, inHubMode, sellerInfo, onPickSupplier }) {
  const stages = ["booked", "picked_up", "in_transit", "out_for_delivery", "delivered"];
  const idx = Math.max(0, stages.indexOf(s.status));
  const meta = CARRIER_META[s.carrier?.replace(" Freight", "").replace(" Ground", "").replace(" Express", "")] || { color: "bg-foreground", letter: s.carrier?.[0] || "?" };
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`shipment-${s.tracking}`}>
      <div className="grid md:grid-cols-12 gap-4 items-start">
        <div className="md:col-span-1"><div className={`w-11 h-11 rounded-xl ${meta.color} text-white grid place-items-center display`}>{meta.letter}</div></div>
        <div className="md:col-span-5">
          {/* Iter 157 · Phase 3 — supplier chip per row in Hub mode for
              instant visual grouping + a click-target to context-switch. */}
          {inHubMode && sellerInfo?.seller_name && (
            <button
              onClick={(e) => { e.stopPropagation(); onPickSupplier?.(sellerInfo.seller_id); }}
              className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-wider text-foreground/60 hover:text-foreground hover:underline mb-1"
              data-testid={`shipment-supplier-chip-${s.tracking}`}
            >
              <Truck className="w-2.5 h-2.5"/>{sellerInfo.seller_name}
            </button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{s.product || s.carrier}</span>
            <span className="chip bg-secondary mono text-[10px]">{s.tracking}</span>
            {s.type && <span className="chip bg-accent/20 border border-accent/40 text-[10px]">{s.type.toUpperCase()}</span>}
          </div>
          <div className="text-xs text-foreground/55 mt-1.5 inline-flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{s.origin} → {s.destination}</div>
        </div>
        <div className="md:col-span-2"><Mini l="Ship date" v={new Date(s.created_at || Date.now()).toLocaleDateString(undefined, { month: "short", day: "numeric" })}/></div>
        <div className="md:col-span-2"><Mini l="ETA" v={s.eta ? new Date(s.eta).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}/></div>
        <div className="md:col-span-2 text-right">
          <div className="display text-lg">{money(s.cost || 0)}</div>
          <span className={`chip ${s.status === "delivered" ? "bg-accent/30 border border-accent/40" : "bg-foreground text-background"} mt-1`}><Clock className="w-3 h-3"/>{s.status?.replace(/_/g, " ")}</span>
        </div>
      </div>

      {/* 5-step progress */}
      <div className="mt-4 flex items-center gap-2">
        {stages.map((st, i) => (
          <div key={st} className="flex items-center gap-2 flex-1 last:flex-initial">
            {i <= idx ? <CheckCircle2 className="w-4 h-4 text-foreground shrink-0"/> : <Circle className="w-4 h-4 text-foreground/30 shrink-0"/>}
            <div className="text-[9px] uppercase tracking-widest text-foreground/55 capitalize">{st.replace(/_/g, " ")}</div>
            {i < stages.length - 1 && <div className={`flex-1 h-0.5 ${i < idx ? "bg-foreground" : "bg-border"}`}/>}
          </div>
        ))}
      </div>

      {/* Action row */}
      <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-1.5">
        {s.tracking_url ? (
          <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`live-${s.tracking}`}
            className="text-[11px] inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 hover:border-foreground/40 hover:bg-secondary/40 transition-colors">
            <MapPin className="w-3 h-3"/>Live track
          </a>
        ) : (
          <ActionBtn icon={MapPin} onClick={() => toast.info(`No live tracking yet for ${s.tracking}`)}>Live track</ActionBtn>
        )}
        {s.label_url ? (
          <a href={s.label_url} target="_blank" rel="noopener noreferrer" data-testid={`bol-${s.tracking}`}
            className="text-[11px] inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 hover:border-foreground/40 hover:bg-secondary/40 transition-colors">
            <FileText className="w-3 h-3"/>Label / BOL
          </a>
        ) : (
          <ActionBtn icon={FileText} onClick={() => toast.info(`Label not available for ${s.tracking}`)} testid={`bol-${s.tracking}`}>Label / BOL</ActionBtn>
        )}
        <ActionBtn icon={Receipt} onClick={() => toast.success("Invoice downloaded")}>Invoice</ActionBtn>
        <ActionBtn icon={AlertTriangle} onClick={() => toast.info("Exception filed (mock)")}>Exception</ActionBtn>
        <ActionBtn icon={Check} onClick={() => toast.success("Proof of delivery downloaded")}>POD</ActionBtn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Live tracking
// ─────────────────────────────────────────────────────────────────────────────
function TrackTab({ shipments, persona = "buyer" }) {
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [externalAdded, setExternalAdded] = useState([]);
  const [externalInput, setExternalInput] = useState("");
  const [externalCarrier, setExternalCarrier] = useState("auto");
  const [liveTrack, setLiveTrack] = useState(null);    // { events, status, eta }
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState(null);
  const [freightDocs, setFreightDocs] = useState([]);   // [{type:'bol'|'pod', url, filename}]

  // Default to first active shipment
  useEffect(() => {
    if (!active && shipments.length > 0) {
      setActive(shipments.filter(s => s.status !== "delivered")[0] || shipments[0]);
    }
  }, [shipments, active]);

  // Hydrate any external trackers the buyer previously saved (server-side
  // persistence — replaces the old in-memory fake list).
  useEffect(() => {
    api.get("/buyer/external-shipments")
      .then(r => setExternalAdded(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Pull live tracking events — freight (Warp) uses the unified freight-track
  // endpoint which merges Warp API + local event stream + GPS trail. Parcel
  // (Verqo Shipping) uses the classic /shipping/track/{carrier}/{tn}.
  useEffect(() => {
    if (!active?.tracking) { setLiveTrack(null); setTrackError(null); return; }
    setTrackLoading(true);
    setTrackError(null);
    setLiveTrack(null);
    const isFreight = (active.mode === "freight") || (active.provider === "warp") || (active.provider === "verqo-freight");
    if (isFreight) {
      const shipRef = active.id || active.warp_order_id || active.tracking;
      api.get(`/shipping/freight-track/${encodeURIComponent(shipRef)}`)
        .then(r => setLiveTrack(r.data))
        .catch(e => setTrackError(e.response?.data?.detail || "Live freight tracking unavailable for this shipment"))
        .finally(() => setTrackLoading(false));
      // Fetch BOL/POD availability in parallel
      api.get(`/shipping/freight-documents/${encodeURIComponent(shipRef)}`)
        .then(r => setFreightDocs(r.data?.documents || []))
        .catch(() => setFreightDocs([]));
      return;
    }
    setFreightDocs([]);
    if (!active.carrier) { setLiveTrack(null); setTrackError(null); setTrackLoading(false); return; }
    // Map common carrier display names → Verqo Shipping carrier tokens
    const carrierToken = (c) => {
      const k = (c || "").toLowerCase();
      if (k.includes("ups"))   return "ups";
      if (k.includes("usps"))  return "usps";
      if (k.includes("fedex")) return "fedex";
      if (k.includes("dhl"))   return "dhl_express";
      if (k.includes("ontrac")) return "ontrac";
      return "verqo-shipping";
    };
    const token = carrierToken(active.carrier);
    api.get(`/shipping/track/${token}/${encodeURIComponent(active.tracking)}`)
      .then(r => setLiveTrack(r.data))
      .catch(e => setTrackError(e.response?.data?.detail || "Live tracking unavailable for this number"))
      .finally(() => setTrackLoading(false));
  }, [active?.id, active?.tracking, active?.carrier, active?.mode, active?.provider]);

  const all = useMemo(() => [...shipments, ...externalAdded], [shipments, externalAdded]);
  const filtered = useMemo(() => {
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(s =>
      s.tracking?.toLowerCase().includes(q) ||
      s.carrier?.toLowerCase().includes(q) ||
      s.origin?.toLowerCase().includes(q) ||
      s.destination?.toLowerCase().includes(q)
    );
  }, [all, search]);

  const detectCarrier = (t) => {
    if (/^1Z/i.test(t)) return "UPS";
    if (/^\d{12,15}$/.test(t)) return "FedEx";
    if (/^9\d{15,21}$/.test(t)) return "USPS";
    if (/^\d{10}$/.test(t)) return "DHL";
    return "Unknown";
  };

  const addExternal = async () => {
    const t = externalInput.trim();
    if (!t) return toast.error("Enter a tracking number");
    if (all.find(s => s.tracking === t)) return toast.error("Already tracking this number");
    try {
      const { data } = await api.post("/buyer/external-shipments", {
        tracking: t,
        carrier: externalCarrier === "auto" ? null : externalCarrier,
      });
      setExternalAdded(prev => prev.find(p => p.tracking === data.tracking) ? prev : [...prev, data]);
      setActive(data);
      setExternalInput("");
      toast.success(`Now tracking ${data.tracking} · ${data.carrier}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save tracking number");
    }
  };

  if (all.length === 0 && !active) {
    return (
      <div data-testid="track-view-empty">
        <SearchAndAdd
          search={search} setSearch={setSearch}
          input={externalInput} setInput={setExternalInput}
          carrier={externalCarrier} setCarrier={setExternalCarrier}
          onAdd={addExternal}
        />
        <div className="text-center py-20 text-foreground/45 text-sm">No shipments to track yet — add a tracking number above.</div>
      </div>
    );
  }

  if (!active) return <div className="text-center py-20 text-foreground/45 text-sm">Loading…</div>;

  return (
    <div data-testid="track-view">
      <SearchAndAdd
        search={search} setSearch={setSearch}
        input={externalInput} setInput={setExternalInput}
        carrier={externalCarrier} setCarrier={setExternalCarrier}
        onAdd={addExternal}
      />

      {/* Tracking list — pick which shipment to view */}
      {filtered.length > 1 && (
        <div className="mt-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="tracking-list">
          {filtered.map(s => {
            const isActive = active?.tracking === s.tracking;
            return (
              <button key={s.id || s.tracking} onClick={() => setActive(s)} data-testid={`track-pick-${s.tracking}`}
                className={`text-left rounded-xl border p-3.5 transition-colors ${isActive ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/40"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[11px] mono uppercase tracking-wider text-foreground/55">{s.carrier}{s.external && <span className="ml-1.5 text-amber-700">· external</span>}</div>
                    <div className="font-medium text-sm mt-0.5 truncate">{s.tracking}</div>
                    <div className="text-[11px] text-foreground/55 mt-1 inline-flex items-center gap-1"><MapPin className="w-2.5 h-2.5"/>{s.origin} → {s.destination}</div>
                  </div>
                  <span className={`chip text-[9px] shrink-0 ${s.status === "delivered" ? "bg-accent/30 border border-accent/40" : "bg-foreground text-background"}`}>
                    {s.status?.replace(/_/g, " ")}
                  </span>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-6 text-xs text-foreground/45">No shipments match &quot;{search}&quot;</div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div>
          <div className="rounded-2xl bg-white border border-border p-7">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Tracking{active.external && <span className="ml-1.5 text-amber-700">· external</span>}</div>
                <div className="display text-3xl mt-1 tracking-tight">{active.tracking}</div>
                <div className="text-sm text-foreground/55 mt-1">{active.carrier} · {active.origin} → {active.destination}</div>
                {liveTrack?.eta && (
                  <div className="text-xs text-emerald-700 mt-1.5 inline-flex items-center gap-1.5"><Clock className="w-3 h-3"/>ETA {new Date(liveTrack.eta).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
                )}
              </div>
              <span className="chip bg-foreground text-background" data-testid="track-status">{(liveTrack?.status || active.status)?.replace(/_/g, " ")}</span>
            </div>

            <div className="mt-6 rounded-2xl bg-secondary/40 border border-border p-6 text-center" data-testid="track-route-visualization">
              {(() => {
                const isFreight = (active.mode === "freight") || (active.provider === "warp") || (active.provider === "verqo-freight");
                // Fallback to fields already on the shipment record so the map
                // renders instantly, even before liveTrack loads. liveTrack
                // overrides once it arrives with fresher GPS data.
                const trail   = liveTrack?.gps_trail   || active.gps_trail   || [];
                const current = liveTrack?.current_location || active.current_location;
                const origin  = liveTrack?.origin_addr || active.origin_addr;
                const dest    = liveTrack?.destination_addr || active.destination_addr;
                const status  = liveTrack?.status || active.status;
                const progress = liveTrack?.progress_pct ?? active.progress_pct;
                if (isFreight && (trail.length || current || (origin && dest))) {
                  return (
                    <div className="text-left -m-6">
                      <LiveFreightTrackingMap
                        trail={trail}
                        current={current}
                        origin={origin}
                        destination={dest}
                        status={status}
                        progressPct={progress}
                      />
                    </div>
                  );
                }
                return (
                  <>
                    <Globe className="w-12 h-12 text-foreground/30 mx-auto"/>
                    <div className="text-xs text-foreground/55 mt-3">Live route visualization</div>
                    <div className="display text-base mt-2">{active.origin} → {active.destination}</div>
                    {active.tracking_url && (
                      <a href={active.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-carrier-link"
                        className="inline-flex items-center text-[11px] mono mt-3 text-foreground/60 hover:text-foreground">
                        Open on carrier site <ArrowRight className="w-2.5 h-2.5 ml-1"/>
                      </a>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="mt-6">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-3 flex items-center gap-2">
                Tracking events
                {trackLoading && <span className="text-foreground/45">· fetching…</span>}
                {!trackLoading && liveTrack && <span className="chip text-[9px] bg-emerald-100 text-emerald-700 border border-emerald-200">LIVE tracking</span>}
              </div>
              {trackLoading ? (
                <div className="text-xs text-foreground/55 py-6">Fetching live tracking from carrier…</div>
              ) : trackError ? (
                <div className="text-xs text-foreground/55 py-6 rounded-xl border border-dashed border-border px-4">
                  {trackError}. {active.tracking_url && <>Open <a href={active.tracking_url} target="_blank" rel="noopener noreferrer" className="underline">carrier site</a> for live updates.</>}
                </div>
              ) : (liveTrack?.events && liveTrack.events.length > 0) ? (
                <div className="border-l-2 border-border pl-5 space-y-4" data-testid="track-events-live">
                  {liveTrack.events.slice().reverse().map((e, i) => (
                    <div key={i} className="relative">
                      <div className={`absolute -left-[26px] w-3 h-3 rounded-full ${i === 0 ? "bg-accent ring-4 ring-accent/30" : "bg-foreground/30"}`}/>
                      <div className="text-sm font-medium capitalize">{(e.status || "Update").replace(/_/g, " ")}</div>
                      <div className="text-xs text-foreground/55 mt-0.5">
                        {e.details || ""}
                        {e.location && (e.location.city || e.location.state) ? ` · ${e.location.city || ""}${e.location.state ? ", " + e.location.state : ""}` : ""}
                        {e.date ? ` · ${new Date(e.date).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-foreground/55 py-6 rounded-xl border border-dashed border-border px-4" data-testid="track-events-empty">
                  No carrier scans yet. The first scan typically appears within a few hours of pickup.
                </div>
              )}
            </div>
        </div>
      </div>

      <aside className="space-y-3">
        <div className="rounded-2xl bg-white border border-border p-5 sticky top-32">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Shipment details</div>
          <dl className="mt-3 space-y-2 text-xs">
            <SummaryRow l="Carrier" v={active.carrier}/>
            {(() => {
              const isFreight = (active.mode === "freight") || (active.provider === "warp") || (active.provider === "verqo-freight");
              if (isFreight) {
                const svcParts = [active.shipment_type, active.service_level].filter(Boolean);
                const service  = svcParts.length ? svcParts.join(" · ") : "Freight";
                const pallets  = active.pallet_count || active.parcels?.length || null;
                const wt       = active.total_weight_lbs || active.weight_lbs || null;
                const dist     = active.distance_mi ? `${Math.round(active.distance_mi).toLocaleString()} mi` : null;
                return (
                  <>
                    <SummaryRow l="Service"  v={service}/>
                    {pallets && <SummaryRow l="Pallets" v={`${pallets}`}/>}
                    {wt      && <SummaryRow l="Weight"  v={`${wt} lbs`}/>}
                    {dist    && <SummaryRow l="Route"   v={dist}/>}
                    {active.transit_days && <SummaryRow l="Transit" v={`${active.transit_days} business days`}/>}
                  </>
                );
              }
              return (
                <>
                  <SummaryRow l="Service"    v={active.type || active.service_level || "Parcel"}/>
                  {active.weight_lbs      && <SummaryRow l="Weight"     v={`${active.weight_lbs} lbs`}/>}
                  {active.dimensions      && <SummaryRow l="Dimensions" v={active.dimensions}/>}
                </>
              );
            })()}
            <SummaryRow l="Reference" v={active.tracking}/>
            <SummaryRow l="Cost" v={money(active.cost || 0)}/>
          </dl>
          <div className="mt-4 grid gap-1.5">
            {(() => {
              const isFreight = (active.mode === "freight") || (active.provider === "warp") || (active.provider === "verqo-freight");
              if (isFreight) {
                const shipRef = active.id || active.warp_order_id || active.tracking;
                const bol = freightDocs.find(d => d.type === "bol");
                const pod = freightDocs.find(d => d.type === "pod");
                const openDoc = (type) => {
                  api.get(`/shipping/freight-documents/${encodeURIComponent(shipRef)}/${type}/download`, {
                    responseType: "blob",
                  }).then((r) => {
                    const blob = new Blob([r.data], { type: r.headers?.["content-type"] || "application/pdf" });
                    const url  = URL.createObjectURL(blob);
                    window.open(url, "_blank", "noopener,noreferrer");
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  }).catch((e) => toast.error(e.response?.data?.detail || `${type.toUpperCase()} not available yet`));
                };
                return (
                  <>
                    {bol ? (
                      <Button variant="outline" size="sm" onClick={() => openDoc("bol")} className="rounded-full" data-testid="track-bol-btn">
                        <Download className="w-3 h-3 mr-1.5"/>Download BOL
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => toast.info("BOL available after pickup")} className="rounded-full opacity-60" data-testid="track-bol-btn"><Download className="w-3 h-3 mr-1.5"/>BOL — pending pickup</Button>
                    )}
                    {/* Blanket / blind BOL — seller-only. Never shown to buyers
                        (the whole point is that the buyer doesn't know the
                        shipper's identity). Downloads a BOL variant with the
                        shipper block redacted to "Verqo Freight Broker". */}
                    {bol && persona === "seller" && (
                      <Button variant="outline" size="sm" onClick={() => openDoc("bol-blanket")} className="rounded-full" data-testid="track-bol-blanket-btn">
                        <Download className="w-3 h-3 mr-1.5"/>Download Blanket BOL
                      </Button>
                    )}
                    {pod ? (
                      <Button variant="outline" size="sm" onClick={() => openDoc("pod")} className="rounded-full" data-testid="track-pod-btn">
                        <Download className="w-3 h-3 mr-1.5"/>Download POD
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => toast.info("POD available after delivery")} className="rounded-full opacity-60" data-testid="track-pod-btn"><Download className="w-3 h-3 mr-1.5"/>POD — pending delivery</Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openDoc("commercial-invoice")} className="rounded-full" data-testid="track-ci-btn">
                      <FileText className="w-3 h-3 mr-1.5"/>Commercial Invoice
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDoc("packing-list")} className="rounded-full" data-testid="track-pl-btn">
                      <FileText className="w-3 h-3 mr-1.5"/>Packing List
                    </Button>
                  </>
                );
              }
              // Parcel path (unchanged)
              return active.label_url ? (
                <a href={active.label_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-xs rounded-full border border-border h-8 px-3 hover:bg-secondary/40 transition-colors" data-testid="track-bol">
                  <Download className="w-3 h-3 mr-1.5"/>Download label / BOL
                </a>
              ) : (
                <Button variant="outline" size="sm" onClick={() => toast.info("Label not available for this shipment")} className="rounded-full"><Download className="w-3 h-3 mr-1.5"/>Download BOL</Button>
              );
            })()}
            {(() => {
              const isFreight = (active.mode === "freight") || (active.provider === "warp") || (active.provider === "verqo-freight");
              if (!isFreight) {
                return (
                  <>
                    <Button variant="outline" size="sm" onClick={() => toast.success("Invoice downloaded")} className="rounded-full"><FileText className="w-3 h-3 mr-1.5"/>Invoice</Button>
                    <Button variant="outline" size="sm" onClick={() => toast.info("Exception filed (mock)")} className="rounded-full"><AlertTriangle className="w-3 h-3 mr-1.5"/>File exception</Button>
                  </>
                );
              }
              const shipRef = active.id || active.warp_order_id || active.tracking;
              const canCancel = !["delivered", "cancelled", "cancel_requested"].includes(active.status);
              const showInvoice = () => {
                // Download the freight invoice PDF (seller-branded, includes
                // wallet payment status). Falls back to a toast breakdown if
                // the PDF endpoint errors so the user still gets info.
                api.get(`/shipping/freight-documents/${encodeURIComponent(shipRef)}/invoice/download`, {
                  responseType: "blob",
                })
                  .then((r) => {
                    const blob = new Blob([r.data], { type: r.headers?.["content-type"] || "application/pdf" });
                    const url  = URL.createObjectURL(blob);
                    window.open(url, "_blank", "noopener,noreferrer");
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  })
                  .catch(() => {
                    // Fallback: read the JSON breakdown and toast it
                    api.get(`/shipping/freight-invoice/${encodeURIComponent(shipRef)}`)
                      .then((r) => {
                        const d = r.data;
                        const accs = (d.accessorials || []).map(a => `\n  · ${a.label}: $${a.amount}`).join("");
                        toast.message(`Invoice ${d.order_id}`, {
                          description: `Transit $${d.transit_cost} · Fuel $${d.fuel_cost}${accs}\n———\nGrand total: $${d.grand_total} · ${d.status}`,
                          duration: 8000,
                        });
                      })
                      .catch((e) => toast.error(e.response?.data?.detail || "Invoice unavailable"));
                  });
              };
              const doCancel = () => {
                // First fetch the fee preview so we can show the user the tier
                api.get(`/shipping/freight-cancel/${encodeURIComponent(shipRef)}/quote`)
                  .then((r) => {
                    const q = r.data;
                    if (!q.cancellable) {
                      toast.error(`Cannot cancel — shipment is ${q.current_status}`);
                      return;
                    }
                    const fee = q.fee_usd || 0;
                    const line = fee === 0
                      ? "This shipment can be cancelled at no charge (carrier hasn't accepted yet)."
                      : `Cancelling now will charge a $${fee} ${q.tier} fee — ${q.reason}.`;
                    if (!window.confirm(`${line}\n\nProceed with cancellation?`)) return;
                    api.post(`/shipping/freight-cancel/${encodeURIComponent(shipRef)}`, {
                      reason: `Requested by ${(active.mode === "freight") ? "buyer/seller" : "operator"} via portal`,
                      accept_fee: fee > 0,
                    })
                      .then((r2) => {
                        toast.success(r2.data?.message || "Cancel request submitted");
                        setTimeout(() => window.location.reload(), 1500);
                      })
                      .catch((e) => toast.error(e.response?.data?.detail || "Cancel failed"));
                  })
                  .catch((e) => toast.error(e.response?.data?.detail || "Could not fetch cancel quote"));
              };
              return (
                <>
                  <Button variant="outline" size="sm" onClick={showInvoice} className="rounded-full" data-testid="track-invoice-btn"><FileText className="w-3 h-3 mr-1.5"/>Invoice</Button>
                  {canCancel ? (
                    <Button variant="outline" size="sm" onClick={doCancel} className="rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="track-cancel-btn"><AlertTriangle className="w-3 h-3 mr-1.5"/>Cancel shipment</Button>
                  ) : (
                    <Button variant="outline" size="sm" disabled className="rounded-full opacity-60"><AlertTriangle className="w-3 h-3 mr-1.5"/>{active.status === "cancelled" ? "Cancelled" : active.status === "cancel_requested" ? "Cancel pending" : "Delivered"}</Button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </aside>
      </div>
    </div>
  );
}

function SearchAndAdd({ search, setSearch, input, setInput, carrier, setCarrier, onAdd }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5 grid md:grid-cols-2 gap-4 items-end" data-testid="track-search-add">
      {/* Search existing tracking numbers */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-2">Search your shipments</div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Tracking #, carrier, origin, destination…"
            className="pl-9 h-11" data-testid="track-search"/>
        </div>
      </div>
      {/* Add external tracking */}
      <div>
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-2">Add tracking from another carrier</div>
        <div className="flex gap-1.5">
          <select value={carrier} onChange={e => setCarrier(e.target.value)}
            className="h-11 rounded-md border border-input bg-white px-2 text-xs">
            <option value="auto">Auto-detect</option>
            <option value="FedEx">FedEx</option>
            <option value="UPS">UPS</option>
            <option value="USPS">USPS</option>
            <option value="DHL">DHL</option>
            <option value="Other">Other</option>
          </select>
          <Input value={input} onChange={e => setInput(e.target.value)}
            placeholder="Paste tracking number"
            onKeyDown={e => e.key === "Enter" && onAdd()}
            className="flex-1 h-11" data-testid="track-add-input"/>
          <Button onClick={onAdd} data-testid="track-add-btn"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-4">
            <Plus className="w-3.5 h-3.5 mr-1"/>Track
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Documents & BOL
// ─────────────────────────────────────────────────────────────────────────────
function DocsTab({ shipments }) {
  return (
    <div data-testid="docs-tab">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Documents archive</div>
          <h2 className="display text-2xl mt-1">Bills of lading &amp; commercial invoices</h2>
        </div>
        <Button variant="outline" onClick={() => toast.success("All documents zipped")} className="rounded-full h-9 px-4">
          <Download className="w-3.5 h-3.5 mr-1.5"/>Export all
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {shipments.slice(0, 12).map((s, i) => (
          <div key={s.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`doc-${i}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Bill of lading</div>
                <div className="display text-base mt-1">BOL-{String(s.id).slice(-4).toUpperCase()}</div>
              </div>
              <FileText className="w-5 h-5 text-foreground/30"/>
            </div>
            <div className="text-xs text-foreground/55 mt-3">{s.carrier} · {s.tracking}</div>
            <div className="text-[10px] text-foreground/45 mono mt-1">Generated {new Date(s.created_at || Date.now()).toLocaleDateString()}</div>
            {s.label_url ? (
              <a href={s.label_url} target="_blank" rel="noopener noreferrer" data-testid={`doc-link-${i}`}
                className="mt-4 w-full inline-flex items-center justify-center rounded-full h-9 border border-border hover:bg-secondary/40 transition-colors text-sm">
                <Download className="w-3 h-3 mr-1.5"/>Download
              </a>
            ) : (
              <Button size="sm" variant="outline" onClick={() => toast.info("Label not available — was this booked before live mode?")} className="mt-4 w-full rounded-full h-9">
                <Download className="w-3 h-3 mr-1.5"/>Download
              </Button>
            )}
          </div>
        ))}
        {shipments.length === 0 && <div className="col-span-full text-center py-12 text-foreground/45 text-sm">No documents yet — book a shipment first</div>}
      </div>

      <div className="mt-10 rounded-2xl border border-border bg-white p-7">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Commercial invoice generator</div>
        <h3 className="display text-2xl mt-1">Auto-generate customs &amp; commercial invoices</h3>
        <div className="mt-5 grid md:grid-cols-4 gap-3">
          <div><Label>Document type</Label>
            <select className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
              <option>Commercial Invoice</option><option>Packing List</option><option>Bill of Lading</option><option>Certificate of Origin</option><option>Customs Declaration</option>
            </select>
          </div>
          <div><Label>Currency</Label>
            <select className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm"><option>USD</option><option>EUR</option><option>GBP</option></select>
          </div>
          <div><Label>Incoterms</Label>
            <select className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm"><option>DAP</option><option>DDP</option><option>EXW</option><option>FOB</option><option>CIF</option></select>
          </div>
          <div className="flex items-end">
            <Button onClick={() => toast.success("Document generated")} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11">Generate</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Rate compare
// ─────────────────────────────────────────────────────────────────────────────
function RatesCompareTab() {
  const [oZip, setOZip] = useState("10001");
  const [dZip, setDZip] = useState("90001");
  const [w, setW] = useState(48);
  const [type, setType] = useState("parcel");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(true);

  const compare = async () => {
    setLoading(true);
    try {
      const fromAddr = { name: "From", street1: "1 Main St", city: "Origin", state: "NY", zip: oZip, country: "US" };
      const toAddr   = { name: "To",   street1: "1 Main St", city: "Destination", state: "CA", zip: dZip, country: "US" };
      const parcels  = [{ length: 12, width: 10, height: 8, distance_unit: "in", weight: Number(w) || 1, mass_unit: "lb" }];
      const { data } = await api.post("/shipping/rates", {
        order_id: "compare", origin: oZip, destination: dZip,
        from_addr: fromAddr, to_addr: toAddr, parcels,
        mode: type === "parcel" ? "parcel" : "freight",
      });
      const rawRates = data?.rates || [];
      setLive(!!data?.live);
      const costs = rawRates.map(r => Number(r.cost ?? r.amount ?? 0)).filter(n => n > 0);
      const maxCost = costs.length ? Math.max(...costs) : 0;
      const enriched = rawRates.map(r => {
        const cost = Number(r.cost ?? r.amount ?? 0);
        const carrier = r.carrier || "Carrier";
        const metaKey = carrier.replace(/\s+(Freight|Ground|Express|Standard|Priority).*$/, "").trim();
        const meta = CARRIER_META[metaKey] || CARRIER_META[carrier] || { net: "Direct", color: "bg-foreground", letter: (carrier[0] || "?").toUpperCase() };
        return {
          carrier,
          service: r.service || "",
          carrier_logo: r.carrier_logo,
          cost,
          days: r.estimated_days ?? r.days ?? 3,
          savings_pct: maxCost > 0 ? Math.max(0, Math.round((1 - cost / maxCost) * 100)) : 0,
          ...meta,
        };
      });
      setResults(enriched);
      if (enriched.length === 0) toast.info("No rates returned for this lane");
      else toast.success(`${enriched.length} ${data?.live ? "live" : "demo"} rates fetched`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch rates");
      setResults([]);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="rates-tab">
      <div className="rounded-2xl border border-border bg-white p-7">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Quick rate comparison</div>
        <h2 className="display text-2xl mt-1">Compare live rates without booking</h2>
        <div className="mt-5 grid md:grid-cols-5 gap-3">
          <div><Label>Origin ZIP</Label><Input value={oZip} onChange={e => setOZip(e.target.value)} className="mt-1.5 h-11" data-testid="ozip"/></div>
          <div><Label>Destination ZIP</Label><Input value={dZip} onChange={e => setDZip(e.target.value)} className="mt-1.5 h-11" data-testid="dzip"/></div>
          <div><Label>Weight (lbs)</Label><Input type="number" value={w} onChange={e => setW(e.target.value)} className="mt-1.5 h-11"/></div>
          <div><Label>Type</Label>
            <select value={type} onChange={e => setType(e.target.value)} className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
              <option value="parcel">Parcel</option><option value="ltl">LTL</option><option value="ftl">FTL</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={compare} disabled={loading} data-testid="compare-go" className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11">
              <Search className="w-4 h-4 mr-1.5"/>{loading ? "Fetching…" : "Get rates"}
            </Button>
          </div>
        </div>
      </div>

      {loading && !results && (
        <div className="mt-6 rounded-2xl border border-border bg-white p-5 flex items-center gap-4" data-testid="compare-loading-panel">
          <div className="relative w-10 h-10 shrink-0">
            <div className="absolute inset-0 rounded-full border-2 border-secondary"/>
            <div className="absolute inset-0 rounded-full border-2 border-foreground border-t-transparent animate-spin"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">Fetching live rates from the Verqo Network</div>
            <div className="text-[11px] text-foreground/60 mt-0.5">Pinging {type === "parcel" ? "parcel network" : "75+ freight carriers"} · comparing prices, transit, and on-time performance…</div>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/40">~3-5s</div>
        </div>
      )}

      {results && (
        <div className="mt-6 space-y-2" data-testid="compare-results">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-2 flex items-center gap-2">
            <span>{oZip} → {dZip} · {w} lbs · {type}</span>
            <span className={`chip text-[9px] border ${live ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>{live ? "LIVE" : "DEMO"}</span>
          </div>
          {results.length === 0 && <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-foreground/55">No rates returned. Try a different ZIP, weight, or type.</div>}
          {results.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-center rounded-2xl border border-border bg-white p-4">
              <div className="col-span-1">
                {r.carrier_logo
                  ? <img src={r.carrier_logo} alt={r.carrier} className="w-10 h-10 rounded-xl object-contain bg-white border border-border p-1"/>
                  : <div className={`w-10 h-10 rounded-xl ${r.color || "bg-foreground"} text-white grid place-items-center display`}>{r.letter || (r.carrier?.[0] || "?")}</div>}
              </div>
              <div className="col-span-3"><div className="font-medium">{r.carrier}</div><div className="text-[10px] uppercase mono text-foreground/50 truncate" title={r.service || r.net}>{r.service || r.net}</div></div>
              <div className="col-span-2"><Clock className="w-3 h-3 inline mr-1"/>{r.days}d</div>
              <div className="col-span-2 text-xs text-foreground/55">{r.savings_pct > 0 ? `Saves ${r.savings_pct}%` : "—"}</div>
              <div className="col-span-2 text-right display text-xl">{money(r.cost)}</div>
              <div className="col-span-2 text-right"><Button size="sm" variant="outline" className="rounded-full h-8 px-3 text-xs">View details</Button></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared components ───
function KPI({ l, v, hint, small }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{l}</div>
      <div className={`display ${small ? "text-lg" : "text-2xl"} mt-2 tracking-tight`}>{v}</div>
      {hint && <div className="text-[10px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}

function CarrierBadge({ title, tag, desc, chips, mode = "live" }) {
  const isLive = mode === "live";
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Connected</div>
          <div className="display text-lg mt-1">{title}</div>
          <div className="text-xs text-foreground/55 mt-0.5">{tag}</div>
        </div>
        <span className={`chip text-[10px] border ${isLive ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`} data-testid={`carrier-badge-${isLive ? "live" : "demo"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-emerald-600" : "bg-amber-500"}`}/>
          {isLive ? "Live" : "Demo mode"}
        </span>
      </div>
      <p className="text-xs text-foreground/55 mt-3">{desc}</p>
      <div className="mt-3 flex flex-wrap gap-1">
        {chips.map(c => <span key={c} className="chip bg-secondary text-[10px]">{c}</span>)}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-7 space-y-5">
      <div>
        <h3 className="display text-2xl tracking-tight">{title}</h3>
        {subtitle && <p className="text-sm text-foreground/55 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{children}</div>;
}

function AddressBlock({ label, addr, onChange, prefix }) {
  const u = (k, v) => onChange({ ...addr, [k]: v });
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-2.5">{label}</div>
      <div className="grid md:grid-cols-2 gap-3">
        <Input value={addr.name} onChange={e => u("name", e.target.value)} placeholder="Company / sender name" className="h-11" data-testid={`${prefix}-name`}/>
        <Input value={addr.address1} onChange={e => u("address1", e.target.value)} placeholder="Address line 1" className="h-11" data-testid={`${prefix}-addr`}/>
        <Input value={addr.city} onChange={e => u("city", e.target.value)} placeholder="City" className="h-11" data-testid={`${prefix}-city`}/>
        <div className="grid grid-cols-2 gap-3">
          <Input value={addr.state} onChange={e => u("state", e.target.value)} placeholder="State" className="h-11"/>
          <Input value={addr.zip} onChange={e => u("zip", e.target.value)} placeholder="ZIP" className="h-11"/>
        </div>
      </div>
    </div>
  );
}

function NavRow({ onBack, onNext, nextLabel, testid, nextDisabled = false }) {
  return (
    <div className="flex justify-between items-center pt-2">
      {onBack ? <Button variant="ghost" onClick={onBack} className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1.5"/>Back</Button> : <span/>}
      <Button onClick={onNext} data-testid={testid || "wizard-next"} disabled={nextDisabled} className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5 disabled:opacity-60 disabled:cursor-not-allowed">
        {nextLabel} <ArrowRight className="w-4 h-4 ml-1.5"/>
      </Button>
    </div>
  );
}

function ReviewRow({ l, v }) { return <div className="flex justify-between"><span className="text-foreground/55">{l}</span><span className="font-medium">{v}</span></div>; }
function SummaryRow({ l, v }) { return <div className="flex justify-between"><span className="text-foreground/55">{l}</span><span className="font-medium">{v}</span></div>; }
function Mini({ l, v }) { return <div><div className="text-[9px] mono uppercase tracking-wider text-foreground/45">{l}</div><div className="text-sm">{v}</div></div>; }
function ActionBtn({ icon: Icon, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-[11px] inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1.5 hover:border-foreground/40 hover:bg-secondary/40 transition-colors">
      <Icon className="w-3 h-3"/>{children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SampleBuyerShipmentsPreview — empty-state preview for BuyerShipping. Shows
// what the buyer's tracking list will look like once they have real shipments.
// ─────────────────────────────────────────────────────────────────────────────
function SampleBuyerShipmentsPreview() {
  const [sampleShipment, setSampleShipment] = useState(null);
  return (
    <div data-testid="sample-buyer-shipments-preview" className="rounded-2xl bg-white border border-border overflow-hidden">
      <SamplePreviewBanner noun="Shipments"/>
      <div className="divide-y divide-border">
        {SAMPLE_SHIPMENTS.map(s => {
          const tone = s.status === "delivered" ? "bg-emerald-500/15 text-emerald-700"
                     : s.status === "in_transit" ? "bg-blue-500/15 text-blue-700"
                     : "bg-amber-500/15 text-amber-800";
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => setSampleShipment(s)}
              data-testid={`sample-buyer-shipment-row-${s.id}`}
              className="w-full text-left grid grid-cols-[1fr_1.5fr_120px_130px_120px] gap-3 px-5 py-3 items-center opacity-[0.78] hover:opacity-100 hover:bg-secondary/40 transition-all"
            >
              <div className="font-mono text-[12.5px] text-slate-700 flex items-center gap-1.5">
                {s.order_code}
                <SampleChip />
              </div>
              <div className="text-[13px] text-slate-700 truncate">
                {s.carrier} · {s.service} from {s.seller_name}
              </div>
              <div>
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${tone}`}>
                  {s.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="text-[11.5px] text-slate-500 font-mono truncate">{s.tracking_number}</div>
              <div className="text-right text-[11px] text-foreground/40">Open →</div>
            </button>
          );
        })}
      </div>
      <SampleShipmentDrawer shipment={sampleShipment} onClose={() => setSampleShipment(null)} />
    </div>
  );
}

